import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getWalletStatement, getStaff, exportWalletExcel, exportWalletPdf } from '../lib/api';
import { formatDate, formatTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Wallet, ArrowUp, ArrowDown, Funnel, DownloadSimple, FilePdf, FileXls } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';

export default function WalletStatementPage() {
  const { user } = useAuth();
  const [ledger, setLedger] = useState([]);
  const [summary, setSummary] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');

  const isAdmin = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'assistant' || user?.role === 'accountant';

  const staffOptions = useMemo(() => [
    { value: 'all', label: 'All Staff' },
    ...staffList.map(s => ({ value: s.id, label: s.name }))
  ], [staffList]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const staffId = selectedStaff === 'all' ? undefined : selectedStaff;
      const res = await getWalletStatement(staffId, startDate || undefined, endDate || undefined);
      setLedger(res.data.ledger);
      setSummary(res.data.summary);
      if (isAdmin) {
        const staffRes = await getStaff();
        setStaffList(staffRes.data.filter(s => s.role === 'staff'));
      }
    } catch (err) {
      toast.error('Statement load nahi hua');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedStaff, startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatCurrency = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'Deposit': return 'bg-green-100 text-green-700';
      case 'Booking': return 'bg-red-100 text-red-700';
      case 'Refund': return 'bg-blue-100 text-blue-700';
      case 'Adjustment': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const staffId = selectedStaff === 'all' ? undefined : selectedStaff;
      const res = await exportWalletExcel(staffId, startDate || undefined, endDate || undefined);
      downloadFile(new Blob([res.data]), 'wallet_statement.xlsx');
      toast.success('Excel download ho gaya!');
    } catch (err) { toast.error('Export fail hua'); }
    finally { setExporting(false); }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const staffId = selectedStaff === 'all' ? undefined : selectedStaff;
      const res = await exportWalletPdf(staffId, startDate || undefined, endDate || undefined);
      downloadFile(new Blob([res.data]), 'wallet_statement.pdf');
      toast.success('PDF download ho gaya!');
    } catch (err) { toast.error('Export fail hua'); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-5" data-testid="wallet-statement-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Wallet size={28} weight="fill" className="text-[#002FA7]" /> Wallet Statement
        </h1>
        <div className="flex gap-2">
          <Button onClick={handleExportExcel} disabled={exporting || ledger.length === 0} variant="outline" className="gap-1.5 text-green-700 border-green-300 hover:bg-green-50" data-testid="export-excel-btn">
            <FileXls size={18} weight="fill" /> Excel
          </Button>
          <Button onClick={handleExportPdf} disabled={exporting || ledger.length === 0} variant="outline" className="gap-1.5 text-red-700 border-red-300 hover:bg-red-50" data-testid="export-pdf-btn">
            <FilePdf size={18} weight="fill" /> PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="wallet-filters">
        <div className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-600">
          <Funnel size={16} /> Filters
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {isAdmin && (
            <div>
              <Label className="text-xs text-gray-500">Staff</Label>
              <SearchableSelect
                options={staffOptions}
                value={selectedStaff}
                onValueChange={setSelectedStaff}
                placeholder="All Staff"
                data-testid="wallet-staff-filter"
              />
            </div>
          )}
          <div>
            <Label className="text-xs text-gray-500">From</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="wallet-start-date" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">To</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="wallet-end-date" />
          </div>
        </div>
      </div>

      {/* Search */}
      {isAdmin && (
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search staff, description, agency, category..."
          matchCount={search ? ledger.filter(e =>
            (e.staff_name || '').toLowerCase().includes(search.toLowerCase()) ||
            (e.description || '').toLowerCase().includes(search.toLowerCase()) ||
            (e.category || '').toLowerCase().includes(search.toLowerCase())
          ).length : ledger.length}
          totalCount={ledger.length}
          noun="entries"
          testId="wallet-search"
          inputClassName="pl-8 pr-7 h-9 text-sm"
        />
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="wallet-summary">
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 uppercase">Total Credit</p>
            <p className="text-lg font-bold text-green-600 font-mono flex items-center gap-1">
              <ArrowUp size={16} /> {formatCurrency(summary.total_credits)}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 uppercase">Total Debit</p>
            <p className="text-lg font-bold text-red-600 font-mono flex items-center gap-1">
              <ArrowDown size={16} /> {formatCurrency(summary.total_debits)}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 uppercase">Net Balance</p>
            <p className={`text-lg font-bold font-mono ${summary.net_balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
              {formatCurrency(summary.net_balance)}
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500 uppercase">Transactions</p>
            <p className="text-lg font-bold text-gray-900">{summary.total_entries}</p>
          </div>
        </div>
      )}

      {/* Ledger Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto" data-testid="wallet-ledger">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading...</div>
        ) : (() => {
          // Filter by search
          const filtered = search ? ledger.filter(e =>
            (e.staff_name || '').toLowerCase().includes(search.toLowerCase()) ||
            (e.description || '').toLowerCase().includes(search.toLowerCase()) ||
            (e.category || '').toLowerCase().includes(search.toLowerCase())
          ) : ledger;

          // Group by staff when "All Staff" selected
          const isGrouped = isAdmin && selectedStaff === 'all' && filtered.length > 0;
          const staffGroups = {};
          if (isGrouped) {
            filtered.forEach(e => {
              const key = e.staff_id || 'unknown';
              if (!staffGroups[key]) staffGroups[key] = { name: e.staff_name || 'Unknown', entries: [], credit: 0, debit: 0 };
              staffGroups[key].entries.push(e);
              staffGroups[key].credit += e.credit || 0;
              staffGroups[key].debit += e.debit || 0;
            });
          }

          const renderTable = (entries, showStaffCol) => (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[90px]">Date</TableHead>
                  <TableHead>TXN ID</TableHead>
                  {showStaffCol && <TableHead>Staff</TableHead>}
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right text-green-700">Credit</TableHead>
                  <TableHead className="text-right text-red-700">Debit</TableHead>
                  <TableHead className="text-right font-bold">Balance</TableHead>
                  {isAdmin && <TableHead>Done By</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isAdmin ? 9 : 7} className="text-center text-gray-400 py-8">
                      Koi transaction nahi mila
                    </TableCell>
                  </TableRow>
                ) : entries.map((e, i) => (
                  <TableRow key={`${e.timestamp}-${e.category}-${e.staff_id}-${i}`} className={e.type === 'credit' ? 'bg-green-50/30' : ''} data-testid={`ledger-row-${i}`}>
                    <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                      <div>{formatDate(e.timestamp)}</div>
                      <div className="text-gray-400">{formatTime(e.timestamp)}</div>
                    </TableCell>
                    <TableCell className="text-xs font-mono text-gray-500">{e.transaction_id || e.reference || '-'}</TableCell>
                    {showStaffCol && <TableCell className="text-sm font-medium">{e.staff_name}</TableCell>}
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${getCategoryColor(e.category)}`}>
                        {e.category}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-gray-700 max-w-[200px] truncate">{e.description}</TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {e.credit > 0 ? <span className="text-green-600 font-semibold">+{formatCurrency(e.credit)}</span> : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {e.debit > 0 ? <span className="text-red-600 font-semibold">-{formatCurrency(e.debit)}</span> : '-'}
                    </TableCell>
                    <TableCell className={`text-right font-mono text-sm font-bold ${e.balance >= 0 ? 'text-gray-900' : 'text-red-700'}`}>
                      {formatCurrency(e.balance)}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="text-xs text-gray-500">
                        {e.done_by && (
                          <div>
                            <span>{e.done_by}</span>
                            {e.done_by_role && (
                              <span className={`ml-1 text-[10px] px-1 py-0.5 rounded ${e.done_by_role === 'admin' ? 'bg-blue-100 text-blue-700' : e.done_by_role === 'assistant' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                                {e.done_by_role === 'admin' ? 'Owner' : e.done_by_role === 'assistant' ? 'Asst' : e.done_by_role}
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          );

          if (isGrouped && Object.keys(staffGroups).length > 0) {
            return (
              <div className="space-y-4 p-2">
                {Object.entries(staffGroups).map(([staffId, group]) => (
                  <div key={staffId} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 flex items-center justify-between">
                      <span className="font-semibold text-gray-800">{group.name}</span>
                      <div className="flex gap-4 text-xs font-mono">
                        <span className="text-green-600">Credit: {formatCurrency(group.credit)}</span>
                        <span className="text-red-600">Debit: {formatCurrency(group.debit)}</span>
                        <span className={`font-bold ${(group.credit - group.debit) >= 0 ? 'text-gray-900' : 'text-red-700'}`}>
                          Net: {formatCurrency(group.credit - group.debit)}
                        </span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">{renderTable(group.entries, false)}</div>
                  </div>
                ))}
              </div>
            );
          }

          return renderTable(filtered, isAdmin);
        })()}
      </div>
    </div>
  );
}
